import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { POLL_CURATION_MS, clampWhenHidden } from "@/lib/realtime";
import { useLiveRefetchInterval } from "@/lib/socket";
import type { WorkshopPerson, WorkshopProject } from "@/types/workshop.type";
import { workshopProjectKey, workshopProjectsKey } from "./workshopKeys";

/** GET /workshop/projects/:id — the project plus who owns it and who is in it. */
export type WorkshopProjectDetail = {
  project: WorkshopProject & {
    isOwner: boolean;
    owner: WorkshopPerson;
    members: WorkshopPerson[];
  };
  canWrite: boolean;
};

/** The 409 a delete gets once anyone else has worked in the project. */
export const PROJECT_HAS_COLLABORATORS = "WORKSHOP_PROJECT_HAS_COLLABORATORS";

/**
 * A delete removes the row before the server answers. A poll or a mount refetch
 * can still land with the old list and put the row back, until the delete's own
 * refetch removes it again. A response that started before the delete finished
 * keeps the list already on screen. The id stays hidden until a response that
 * started after the delete comes back without it.
 */
let projectListGeneration = 0;
const pendingProjectDeletes = new Map<string, number>();

function withoutPendingDeletes(list: WorkshopProject[]): WorkshopProject[] {
  if (pendingProjectDeletes.size === 0) return list;
  return list.filter((project) => !pendingProjectDeletes.has(project.id));
}

export function useWorkshopProjects(brandId: string | null) {
  const qc = useQueryClient();
  const listKey = workshopProjectsKey(brandId);
  // Collaborators' changes are pushed while the brand is live; the poll is a net.
  const pollMs = useLiveRefetchInterval(brandId, POLL_CURATION_MS);

  const query = useQuery({
    queryKey: listKey,
    queryFn: async () => {
      const started = projectListGeneration;
      const res = await axiosInstance.get<{ projects: WorkshopProject[] }>(
        `/workshop/brand/${brandId}/projects`,
      );
      const projects = res.data.projects;
      // Started before a delete finished. Its body can still contain the row.
      if (started !== projectListGeneration) {
        return withoutPendingDeletes(qc.getQueryData<WorkshopProject[]>(listKey) ?? projects);
      }
      for (const [id, generation] of pendingProjectDeletes) {
        if (started >= generation && !projects.some((project) => project.id === id)) {
          pendingProjectDeletes.delete(id);
        }
      }
      return withoutPendingDeletes(projects);
    },
    enabled: !!brandId,
    refetchInterval: () => clampWhenHidden(pollMs),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: listKey });
  };

  const createProject = useMutation({
    mutationFn: async (vars: { name: string; description?: string }) => {
      const res = await axiosInstance.post<{ project: WorkshopProject }>(
        `/workshop/brand/${brandId}/projects`,
        vars,
      );
      return res.data.project;
    },
    // The new project opens straight away; its card is in the list by then.
    onSuccess: (project) => {
      qc.setQueryData<WorkshopProject[]>(listKey, (list = []) =>
        list.some((p) => p.id === project.id)
          ? list
          : [{ ...project, isOwner: true, members: [], sessionCount: 0 }, ...list],
      );
      invalidate();
    },
  });

  /** Owner only (the server refuses anyone else). Applied to the card at once. */
  const updateProject = useMutation({
    mutationFn: async (vars: {
      projectId: string;
      name?: string;
      description?: string | null;
      status?: "active" | "archived";
    }) => {
      const { projectId, ...body } = vars;
      const res = await axiosInstance.put<{ project: WorkshopProject }>(
        `/workshop/projects/${projectId}`,
        body,
      );
      return res.data.project;
    },
    onMutate: async ({ projectId, ...patch }) => {
      await qc.cancelQueries({ queryKey: listKey });
      const before = qc.getQueryData<WorkshopProject[]>(listKey)?.find((p) => p.id === projectId);
      qc.setQueryData<WorkshopProject[]>(listKey, (list) =>
        list?.map((p) => (p.id === projectId ? { ...p, ...patch } : p)),
      );
      qc.setQueryData<WorkshopProjectDetail>(workshopProjectKey(projectId), (detail) =>
        detail ? { ...detail, project: { ...detail.project, ...patch } } : detail,
      );
      return { before };
    },
    // Put back only this project's fields: others may have changed meanwhile.
    onError: (_error, { projectId }, context) => {
      const before = context?.before;
      if (!before) return;
      qc.setQueryData<WorkshopProject[]>(listKey, (list) =>
        list?.map((p) => (p.id === projectId ? { ...p, ...before } : p)),
      );
      void qc.invalidateQueries({ queryKey: workshopProjectKey(projectId) });
    },
    onSettled: (_data, _error, { projectId }) => {
      invalidate();
      void qc.invalidateQueries({ queryKey: workshopProjectKey(projectId) });
    },
  });

  /**
   * Gone from the list at once. A project others worked in can't be deleted
   * (409 PROJECT_HAS_COLLABORATORS): it comes back, and the caller offers
   * Archive instead. revert is off so cancelling the in-flight poll does not
   * write the old list back over the row we just removed.
   */
  const deleteProject = useMutation({
    mutationFn: async (projectId: string) => {
      await axiosInstance.delete(`/workshop/projects/${projectId}`);
    },
    onMutate: async (projectId) => {
      projectListGeneration += 1;
      pendingProjectDeletes.set(projectId, projectListGeneration);
      await qc.cancelQueries({ queryKey: listKey }, { silent: true, revert: false });
      const list = qc.getQueryData<WorkshopProject[]>(listKey);
      const index = list?.findIndex((p) => p.id === projectId) ?? -1;
      const removed = index >= 0 ? list![index] : null;
      qc.setQueryData<WorkshopProject[]>(listKey, (current) =>
        current?.filter((p) => p.id !== projectId),
      );
      return { removed, index };
    },
    // In-flight lists started before this must not be written: they still have the row.
    onSuccess: () => {
      projectListGeneration += 1;
    },
    onError: (_error, projectId, context) => {
      pendingProjectDeletes.delete(projectId);
      const removed = context?.removed;
      if (!removed) return;
      qc.setQueryData<WorkshopProject[]>(listKey, (list = []) => {
        if (list.some((p) => p.id === removed.id)) return list;
        const next = [...list];
        next.splice(Math.min(context.index, next.length), 0, removed);
        return next;
      });
    },
    onSettled: invalidate,
  });

  return {
    projects: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    createProject,
    updateProject,
    deleteProject,
  };
}

export function useWorkshopProject(projectId: string | null) {
  return useQuery({
    queryKey: workshopProjectKey(projectId),
    queryFn: async () => {
      const res = await axiosInstance.get<WorkshopProjectDetail>(
        `/workshop/projects/${projectId}`,
      );
      return res.data;
    },
    enabled: !!projectId,
    refetchInterval: () => clampWhenHidden(POLL_CURATION_MS),
  });
}
